<template>
	<div
		class="map"
		:class="{
			'map--contact': contact,
		}">
		<ClientOnly>
			<LMap
				v-if="mapReady && leafletReady"
				ref="map"
				:zoom="isMobile_lg ? 5 : 6"
				:center="[51.918904, 19.1343786]"
				:use-global-leaflet="true"
				:options="mapOptions"
				@ready="onMapReady">
				<LTileLayer
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					attribution='&amp;copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
					layer-type="base"
					name="OpenStreetMap" />
				<template v-for="item in data" :key="item.id">
					<LMarker
						:ref="
							(el) => {
								if (el) markerRefs[item.id] = el
							}
						"
						:lat-lng="[item.lat, item.lng]">
						<LIcon :icon-size="[20, 20]">
							<GlobalIcon v-if="item.airport && !item.meetAndGreet" class="map__icon" name="airplane" :width="20" />
							<GlobalIcon v-else-if="!item.airport && !item.meetAndGreet" class="map__icon" name="location-filled" :width="20" />
							<GlobalIcon v-else-if="item.meetAndGreet || contact" class="map__icon" name="location-bold" :width="20" />
						</LIcon>
						<LPopup :options="{ direction: 'top', offset: [0, -20], closeButton: false }">
							<div class="map__tooltip">
								<GlobalIcon v-if="item.meetAndGreet" class="map__icon" name="location-bold" :width="20" />
								<GlobalIcon v-else-if="item.airport" class="map__icon" name="airplane" :width="20" />
								<GlobalIcon v-else-if="!item.airport" class="map__icon" name="location-filled" :width="20" />
								<div class="map__tooltip-text-container">
									<p class="map__tooltip-text map__tooltip-text--title">
										{{ item.name }}
									</p>
									<p v-if="!item.street?.includes('null')" class="map__tooltip-text map__tooltip-text--street">
										{{ item.street }}
									</p>
									<div class="map__tooltip-text map__tooltip-text--number">
										<GlobalIcon name="call" :height="14" class="header__call-btn__icon" />
										<p>
											{{
												!item.phoneNumber?.includes('null') && item?.phoneNumber
													? formatPhoneNumber(item.phoneNumber)
													: '+48 794 500 550'
											}}
										</p>
									</div>
									<NuxtLink
										v-if="!contact"
										:to="undefined"
										class="map__tooltip-text map__tooltip-text--link"
										@click="handleElementClick(item.id)"
										>{{ $t('components.departments.full_map.tooltip_link') }}</NuxtLink
									>
								</div>
							</div>
						</LPopup>
					</LMarker>
				</template>
				<LControl position="bottomright">
					<div class="map__btn-container">
						<button class="map__btn map__btn--maximize" @click="maximizeMap">
							<GlobalIcon name="maximize" :height="20" />
						</button>
						<button class="map__btn map__btn--gps" aria-label="Get current location" @click="getCurrentLocation">
							<GlobalIcon name="gps" :height="20" />
						</button>
					</div>
				</LControl>
			</LMap>
		</ClientOnly>
	</div>
</template>
<script setup lang="ts">
import { LMap, LTileLayer, LIcon, LPopup, LMarker, LControl } from '@vue-leaflet/vue-leaflet'

const props = defineProps<{
	scroll: boolean
	contact?: boolean
}>()

const map = ref<any>(null)
const markerRefs = ref({})
const mapReady = ref(false)
const leafletReady = useLeafletStyles()
const { locale } = useI18n()
const { isMobile_lg } = useWindowWidth()
const reservationStore = useReservationStore()
const bookingStore = useBookingStore()
const items = ref<ILocationModel[] | undefined>(undefined)
const locationsStore = useLocationsStore()
const userLocationMarker = ref(null)
const L = ref<any>(null)
const mapStore = useMapStore()
const notyf = useNotyfService()
const $t = useI18n().t

const initLeaflet = async () => {
	if (!L.value) {
		const leaflet = await import('leaflet')
		L.value = leaflet.default
	}
	return L.value
}

onMounted(async () => {
	await nextTick()
	mapReady.value = true
	items.value = await locationsStore.fetchLocations()
})

watch(
	() => reservationStore.getAllLocations,
	(newValue) => {
		if (newValue) {
			items.value = newValue
		}
	},
	{
		immediate: true,
		deep: true,
	},
)

watch(
	() => props.scroll,
	() => {
		if (props.scroll === true) {
			map.value?.leafletObject.scrollWheelZoom.enable()
		}
	},
)

watch(isMobile_lg, () => {
	if (isMobile_lg.value) {
		map.value?.leafletObject.dragging.disable()
		map.value?.leafletObject.tapHold.disable()
	} else {
		map.value?.leafletObject.dragging.enable()
		map.value?.leafletObject.tapHold.enable()
	}
})

let invalidateSizeTimeout: ReturnType<typeof setTimeout> | null = null
const fullscreenChangeHandler = () => {
	map.value?.leafletObject?.invalidateSize()
}

const onMapReady = async () => {
	await initLeaflet()
	invalidateSizeTimeout = setTimeout(() => {
		invalidateSizeTimeout = null
		if (map.value?.leafletObject) {
			map.value.leafletObject.invalidateSize()
			if (isMobile_lg.value) {
				map.value.leafletObject.dragging.disable()
				map.value.leafletObject.tapHold.disable()
			} else {
				map.value.leafletObject.dragging.enable()
				map.value.leafletObject.tapHold.enable()
			}
			map.value.leafletObject.on('fullscreenchange', fullscreenChangeHandler)
		}
	}, 100)
}

const data = computed(() => {
	if (props.contact) {
		return items.value
			?.filter((i) => i.purpose !== 'meet_and_greet')
			.map((i) => ({
				id: i.id,
				lng: i.geo_point?.coordinates[0] ?? 53,
				lat: i.geo_point?.coordinates[1] ?? 24,
				airport: i.icon === 'airplane',
				meetAndGreet: i.purpose === 'meet_and_greet',
				phoneNumber: i.phone_number ?? '+48794500550',
				name: locale.value === 'pl' ? i.name : i.nameEn,
				street: `${i.addressStreet} ${i.addressHouseNumber}`,
			}))
	}

	return items.value?.map((i) => ({
		id: i.id,
		lng: i.geo_point?.coordinates[0] ?? 53,
		lat: i.geo_point?.coordinates[1] ?? 24,
		airport: i.icon === 'airplane',
		meetAndGreet: i.purpose === 'meet_and_greet',
		phoneNumber: i.phone_number ?? '+48794500550',
		name: locale.value === 'pl' ? i.name : i.nameEn,
		street: `${i.addressStreet} ${i.addressHouseNumber}`,
	}))
})

const handleElementClick = (id: number) => {
	const item = items.value?.find((i) => i.id === id)
	if (item) {
		bookingStore.setBookerLocations({ start: item, end: bookingStore.getBookerLocations?.end ?? null })
		reservationStore.addLocationToHistory(item.id)
	}
}

const mapOptions = computed(() => ({
	zoomControl: true,
	scrollWheelZoom: false,
}))

const maximizeMap = () => {
	try {
		if (map.value?.leafletObject) {
			if (document.fullscreenElement) {
				document.exitFullscreen()
			} else {
				map.value.leafletObject.getContainer().requestFullscreen()
			}
		}
	} catch (error) {
		notyf?.error((error as Error).message)
	}
}

const getCurrentLocation = async () => {
	try {
		const L = await initLeaflet()

		if ('geolocation' in navigator) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					if (isUnmounted || !map.value?.leafletObject) return
					const { latitude, longitude } = position.coords
					map.value.leafletObject.setView([latitude, longitude], 13)

					// Add a marker for the user's location
					const userIcon = L.icon({
						iconUrl:
							"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='512' viewBox='0 0 320 512'%3E%3Cpath fill='%23ff5c00' d='M112 48a48 48 0 1 1 96 0a48 48 0 1 1-96 0m40 304v128c0 17.7-14.3 32-32 32s-32-14.3-32-32V256.9l-28.6 47.6c-9.1 15.1-28.8 20-43.9 10.9s-20-28.8-10.9-43.9l58.3-97c17.4-28.9 48.6-46.6 82.3-46.6h29.7c33.7 0 64.9 17.7 82.3 46.6l58.3 97c9.1 15.1 4.2 34.8-10.9 43.9s-34.8 4.2-43.9-10.9L232 256.9V480c0 17.7-14.3 32-32 32s-32-14.3-32-32V352z'/%3E%3C/svg%3E",
						iconSize: [25, 41],
						iconAnchor: [12, 41],
						popupAnchor: [1, -34],
					})

					if (userLocationMarker.value) {
						map.value?.leafletObject.removeLayer(userLocationMarker.value)
					}

					userLocationMarker.value = L.marker([latitude, longitude], { icon: userIcon }).addTo(map.value.leafletObject)
				},
				(error) => {
					logger.error('Error getting location:', error.message)
					notyf?.error($t('error.unable_to_retrieve'))
				},
			)
		} else {
			notyf?.error($t('error.geolocation_is_not_supported'))
		}
	} catch (error) {
		logger.error('Error initializing Leaflet:', error)
	}
}

let isUnmounted = false

onBeforeUnmount(() => {
	isUnmounted = true

	if (invalidateSizeTimeout) {
		clearTimeout(invalidateSizeTimeout)
		invalidateSizeTimeout = null
	}

	const leafletObject = map.value?.leafletObject
	if (leafletObject) {
		try {
			leafletObject.off('fullscreenchange', fullscreenChangeHandler)
		} catch {
			// map may already be partially torn down by @vue-leaflet wrapper
		}

		if (userLocationMarker.value) {
			try {
				leafletObject.removeLayer(userLocationMarker.value)
			} catch {
				// layer may already be detached
			}
			userLocationMarker.value = null
		}
	}

	markerRefs.value = {}
})

watch(
	() => mapStore.getClickedLocation,
	(newValue) => {
		if (newValue) {
			const item = items.value?.find((i) => i.id === newValue.id)
			if (item && item?.geo_point?.coordinates[0] && item?.geo_point?.coordinates[1]) {
				setTimeout(() => {
					if (isUnmounted || !map.value?.leafletObject) return
					map.value.leafletObject.setView([item.geo_point.coordinates[1], item.geo_point.coordinates[0]], 13)

					if (mapStore.getOpenTooltip) {
						const marker = markerRefs.value[newValue.id]
						if (marker && marker.leafletObject) {
							marker.leafletObject.openPopup()
						}
					}
				}, 10)
			}
		}
	},
)

const formatPhoneNumber = (phoneNumber: string) => {
	if (!phoneNumber || phoneNumber?.length < 9) return phoneNumber

	const phone = phoneNumber.replace('+', '')
	return `+${phone.slice(0, 2)} ${phone.slice(2, 5)} ${phone.slice(5, 8)} ${phone.slice(8, 11)}`.trim()
}
</script>

<style scoped lang="scss" src="./Map.scss"></style>
